// WHAT: Custom hook for managing SwitchBot credentials state and operations
// WHY:  Centralizes SwitchBot credentials logic to reduce component complexity
// DOMAIN: Settings → SwitchBot
// DEPENDENCIES: SwitchBot services, React hooks
// RISKS:
//   - NOTE: Token and secret are displayed in plain text when editing - security risk

import { useCallback, useEffect, useState } from "react";

import { debugLog } from "@/domains/logging/utils/debugLog";
import {
  loadSwitchBotSecret,
  loadSwitchBotToken,
  saveSwitchBotSecret,
  saveSwitchBotToken,
} from "@/domains/smarthome/services";

/**
 * WHAT:
 *   Loads both SwitchBot token and secret from secure storage in parallel.
 *
 * WHY:
 *   Provides efficient credential loading with error handling for failed operations.
 *
 * @returns {Promise<[null | string, null | string]>} Tuple of token and secret
 *
 */
async function loadCredentials(): Promise<[null | string, null | string]> {
  try {
    const [tokenResult, secretResult] = await Promise.all([
      (loadSwitchBotToken as () => Promise<unknown>)(),
      (loadSwitchBotSecret as () => Promise<unknown>)(),
    ]);
    const token = typeof tokenResult === "string" ? tokenResult : null;
    const secret = typeof secretResult === "string" ? secretResult : null;
    return [token, secret];
  } catch (error) {
    const message =
      error instanceof Error ? error.message : "Credentials konnten nicht geladen werden.";
    throw new Error(message);
  }
}

/**
 * WHAT:
 *   Custom hook that manages SwitchBot credentials state and operations.
 *
 * WHY:
 *   Centralizes all SwitchBot credentials logic to reduce component complexity.
 *
 * @returns {object} SwitchBot credentials state and handlers
 *
 */
export const useSwitchBotCredentials = () => {
  const [token, setToken] = useState("");
  const [secret, setSecret] = useState("");
  const [savedToken, setSavedToken] = useState<null | string | undefined>();
  const [savedSecret, setSavedSecret] = useState<null | string | undefined>();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | undefined>();
  const [editingToken, setEditingToken] = useState(false);
  const [editingSecret, setEditingSecret] = useState(false);

  /**
   * WHAT:
   *   Loads credentials from secure storage on component mount.
   */
  const loadAllCredentials = useCallback(async () => {
    try {
      const [tok, sec] = await loadCredentials();
      setSavedToken(tok);
      setSavedSecret(sec);
      setToken("");
      setSecret("");
    } catch (error) {
      if (error instanceof Error) {
        debugLog("debug", "settings", "SwitchBot credentials load failed", {
          error: error.message,
          file: "useSwitchBotCredentials.ts",
          function: "loadAllCredentials",
          module: "switchbot",
        });
      }
      setSavedToken(null);
      setSavedSecret(null);
      setToken("");
      setSecret("");
    }
  }, []);

  useEffect(() => {
    void loadAllCredentials();
  }, [loadAllCredentials]);

  /**
   * WHAT:
   *   Saves the SwitchBot access token to secure storage.
   */
  const handleSaveToken = useCallback(async () => {
    if (!token) {
      return;
    }

    setLoading(true);
    setMessage(undefined);

    try {
      const saveTokenSafely = saveSwitchBotToken as (value: string) => Promise<unknown>;
      await saveTokenSafely(token);
      setSavedToken(token);
      setToken("");
      setEditingToken(false);
      setMessage("✅ SwitchBot Token saved successfully");
    } catch {
      setMessage("❌ Error saving SwitchBot Token");
    } finally {
      setLoading(false);
    }
  }, [token]);

  /**
   * WHAT:
   *   Saves the SwitchBot secret key to secure storage.
   */
  const handleSaveSecret = useCallback(async () => {
    if (!secret) {
      return;
    }

    setLoading(true);
    setMessage(undefined);

    try {
      const saveSecretSafely = saveSwitchBotSecret as (value: string) => Promise<unknown>;
      await saveSecretSafely(secret);
      setSavedSecret(secret);
      setSecret("");
      setEditingSecret(false);
      setMessage("✅ SwitchBot Secret Key saved successfully");
    } catch {
      setMessage("❌ Error saving SwitchBot Secret Key");
    } finally {
      setLoading(false);
    }
  }, [secret]);

  /**
   * WHAT:
   *   Removes the SwitchBot access token.
   */
  const handleRemoveToken = useCallback(async () => {
    setLoading(true);
    setMessage(undefined);

    try {
      const saveTokenSafely = saveSwitchBotToken as (value: string) => Promise<unknown>;
      await saveTokenSafely("");
      setSavedToken(null);
      setToken("");
      setEditingToken(false);
      setMessage("✅ SwitchBot Token removed");
    } catch {
      setMessage("❌ Error removing SwitchBot Token");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * WHAT:
   *   Removes the SwitchBot secret key.
   */
  const handleRemoveSecret = useCallback(async () => {
    setLoading(true);
    setMessage(undefined);

    try {
      const saveSecretSafely = saveSwitchBotSecret as (value: string) => Promise<unknown>;
      await saveSecretSafely("");
      setSavedSecret(null);
      setSecret("");
      setEditingSecret(false);
      setMessage("✅ SwitchBot Secret Key removed");
    } catch {
      setMessage("❌ Error removing SwitchBot Secret Key");
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    editingSecret,
    editingToken,
    handleRemoveSecret,
    handleRemoveToken,
    handleSaveSecret,
    handleSaveToken,
    loading,
    message,
    savedSecret,
    savedToken,
    secret,
    setEditingSecret,
    setEditingToken,
    setSecret,
    setToken,
    token,
  };
};
